import dayjs from 'dayjs';
import { SubscriptionStatus } from '@benzinga/subscription-manager';
import type { BillingUserSubscription } from '../../../../src/components/Account/Billing';

export type ProductFamily = 'edge' | 'pro' | 'research';

export type SubscriptionGroupId = 'active' | 'trialing' | 'past';

export type SubscriptionTag = 'auto-renew' | 'cancels' | 'ended' | 'exclusive' | 'paused' | 'trial' | 'upgrading';

export interface SubscriptionRowModel {
  autoRenew: boolean | null;
  canCancelTrial: boolean;
  couponLabel: string | null;
  dateLabel: string;
  dateText: string;
  dateTone: 'default' | 'warning';
  family: ProductFamily | null;
  group: SubscriptionGroupId;
  parentPlanName: string | null;
  priceLabel: string;
  productName: string;
  resumesOn: string | null;
  strikePrice: string | null;
  subscription: BillingUserSubscription;
  tag: SubscriptionTag;
}

export interface SubscriptionGroup {
  id: SubscriptionGroupId;
  rows: SubscriptionRowModel[];
}

const PAST_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.canceled,
  SubscriptionStatus.past_due,
  SubscriptionStatus.unpaid,
];

export const formatBillingDate = (date?: string | null): string => (date ? dayjs(date).format('MMM DD, YYYY') : '');

export const formatPrice = (subscription: BillingUserSubscription): string => {
  const price = subscription.finalPrice || subscription.totalPrice || subscription.localizedPrice || '';
  const { interval, intervalCount } = subscription;
  if (!price) return '';
  if (interval === 'lifetime' || interval === 'onetime') return `${price} one-time`;

  const unit = { day: 'day', month: 'mo', week: 'wk', year: 'yr' }[interval];
  if (!unit) return price;
  return intervalCount && intervalCount > 1 ? `${price}/${intervalCount} ${unit}` : `${price}/${unit}`;
};

/**
 * Product family for the shell's `acct-pill` badge. Deliberately conservative:
 * the subscriptions API has no family field, so only the three names we can
 * match with certainty get a badge — everything else shows its product name
 * as plain meta text rather than risking a wrong badge on a paid plan.
 */
export const productFamily = (subscription: BillingUserSubscription): ProductFamily | null => {
  const haystack = `${subscription.productName ?? ''} ${subscription.planName ?? ''}`;
  if (/\bedge\b/i.test(haystack)) return 'edge';
  if (/\bpro\b/i.test(haystack)) return 'pro';
  if (/\bresearch\b/i.test(haystack)) return 'research';
  return null;
};

const couponLabel = (subscription: BillingUserSubscription): string | null => {
  const coupon = subscription.coupon;
  if (!coupon?.code) return null;
  if (typeof coupon.percentOff === 'number' && coupon.percentOff !== 0) {
    return `Coupon ${coupon.code} · ${coupon.percentOff}% off`;
  }
  if (typeof coupon.amountOff === 'number' && coupon.amountOff !== 0) {
    return `Coupon ${coupon.code} · ${coupon.amountOff} off`;
  }
  return `Coupon ${coupon.code}`;
};

const dateState = (
  subscription: BillingUserSubscription,
  group: SubscriptionGroupId,
): Pick<SubscriptionRowModel, 'dateLabel' | 'dateText' | 'dateTone' | 'tag'> => {
  if (subscription.pendingPause) {
    return {
      dateLabel: 'Pauses on',
      dateText: formatBillingDate(subscription.pauseDate),
      dateTone: 'default',
      tag: 'paused',
    };
  }
  if (subscription.reactivate) {
    return {
      dateLabel: 'Upgrades on',
      dateText: formatBillingDate(subscription.currentPeriodEnd),
      dateTone: 'default',
      tag: 'upgrading',
    };
  }
  if (group === 'past') {
    return {
      dateLabel: subscription.canceledOn ? 'Canceled on' : 'Ended',
      dateText: formatBillingDate(subscription.canceledOn ?? subscription.currentPeriodEnd),
      dateTone: 'default',
      tag: 'ended',
    };
  }
  if (subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd) {
    return {
      dateLabel: 'Cancels on',
      dateText: formatBillingDate(subscription.currentPeriodEnd),
      dateTone: 'warning',
      tag: 'cancels',
    };
  }
  if (group === 'trialing') {
    return {
      dateLabel: 'Trial ends on',
      dateText: formatBillingDate(subscription.trialEnd),
      dateTone: 'default',
      tag: 'trial',
    };
  }
  if (!subscription.currentPeriodEnd) {
    return { dateLabel: 'No end date', dateText: 'Exclusive access', dateTone: 'default', tag: 'exclusive' };
  }
  return {
    dateLabel: 'Renews on',
    dateText: formatBillingDate(subscription.currentPeriodEnd),
    dateTone: 'default',
    tag: 'auto-renew',
  };
};

const sortDate = (row: SubscriptionRowModel): number | null => {
  const { subscription } = row;
  const raw =
    row.tag === 'paused'
      ? subscription.pauseDate
      : row.tag === 'trial'
        ? subscription.trialEnd
        : row.tag === 'ended'
          ? (subscription.canceledOn ?? subscription.currentPeriodEnd)
          : subscription.currentPeriodEnd;
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.valueOf() : null;
};

const compareRows = (a: SubscriptionRowModel, b: SubscriptionRowModel): number => {
  const aDate = sortDate(a);
  const bDate = sortDate(b);
  if (aDate !== bDate) {
    if (aDate === null) return 1;
    if (bDate === null) return -1;
    return aDate - bDate;
  }
  return (a.subscription.planName ?? '').localeCompare(b.subscription.planName ?? '');
};

const toRow = (subscription: BillingUserSubscription, group: SubscriptionGroupId): SubscriptionRowModel => {
  const state = dateState(subscription, group);
  // Legacy parity: the switch only exists on a plain active subscription
  // with a period end — never on pending pause/upgrade or exclusive rows.
  const hasSwitch =
    group === 'active' &&
    !subscription.pendingPause &&
    !subscription.pendingUpgrade &&
    Boolean(subscription.currentPeriodEnd);

  return {
    ...state,
    autoRenew: hasSwitch ? subscription.cancelAtPeriodEnd === false : null,
    canCancelTrial: group === 'trialing' && subscription.cancelAtPeriodEnd === false,
    couponLabel: couponLabel(subscription),
    family: productFamily(subscription),
    group,
    parentPlanName: subscription.pendingUpgrade ? (subscription.parentData?.planName ?? null) : null,
    priceLabel: formatPrice(subscription),
    productName: subscription.productName ?? '',
    resumesOn: subscription.pendingPause ? formatBillingDate(subscription.willActivateOn) : null,
    strikePrice:
      subscription.totalPrice && subscription.totalPrice !== subscription.finalPrice ? subscription.totalPrice : null,
    subscription,
  };
};

/**
 * Applies the legacy pending-subscription derivation without mutating the
 * manager's objects: a pending sub with a future activation and no
 * `reactivate` flag is a *pause* (the pause itself started two months
 * earlier, which is all the API tells us), and a pending sub that
 * reactivates an existing one is an *upgrade* taking effect at the parent's
 * period end.
 */
const derivePending = (
  pending: BillingUserSubscription[],
  active: BillingUserSubscription[],
  all: BillingUserSubscription[],
): BillingUserSubscription[] =>
  pending.map(sub => {
    let derived: BillingUserSubscription = { ...sub };

    if (sub.willActivateOn && !sub.reactivate) {
      derived = {
        ...derived,
        parent: active.find(activeSub => activeSub.basePlan === sub.basePlan)?.uuid,
        pauseDate: dayjs(sub.willActivateOn).subtract(2, 'month').toISOString(),
        pendingPause: true,
      };
    }

    const upgradeParent = all.find(candidate => candidate.uuid === sub.parent);
    if (upgradeParent && sub.reactivate) {
      derived = {
        ...derived,
        parentData: upgradeParent,
        pendingUpgrade: true,
        willActivateOn: upgradeParent.currentPeriodEnd,
      };
    }

    return derived;
  });

export const buildSubscriptionGroups = (subscriptions: BillingUserSubscription[]): SubscriptionGroup[] => {
  const list = subscriptions ?? [];
  const active = list.filter(sub => sub.status === SubscriptionStatus.active);
  const trialing = list.filter(sub => sub.status === SubscriptionStatus.trialing);
  const pending = list.filter(sub => sub.status === SubscriptionStatus.pending);
  const past = list.filter(sub => PAST_STATUSES.includes(sub.status));

  const derivedPending = derivePending(pending, active, list);
  const supersededUuids = derivedPending.map(sub => sub.parent);
  const activeRows = active.filter(sub => !supersededUuids.includes(sub.uuid));

  const groups: SubscriptionGroup[] = [
    { id: 'active', rows: [...activeRows, ...derivedPending].map(sub => toRow(sub, 'active')) },
    { id: 'trialing', rows: trialing.map(sub => toRow(sub, 'trialing')) },
    { id: 'past', rows: past.map(sub => toRow(sub, 'past')) },
  ];

  return groups
    .filter(group => group.rows.length > 0)
    .map(group => ({ ...group, rows: [...group.rows].sort(compareRows) }));
};

export const countRows = (groups: SubscriptionGroup[]): number =>
  groups.reduce((total, group) => total + group.rows.length, 0);
